import { isEmpty /*, isNull*/ } from "lodash";
import { DateTime } from "luxon";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import FormControl from "@mui/material/FormControl";
import IconButton from "@mui/material/IconButton";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Select from "@mui/material/Select";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TextField from "@mui/material/TextField";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  redirect,
  useLoaderData,
  useLocation,
  useNavigate,
  // useNavigate,
} from "react-router-dom";
import styled from "styled-components";

// Imported actions
import { getAlertStatusTypes, getFaultTypes } from "../../../actions/alert.js";
import { getEquipmentTypes } from "../../../actions/equipment.js";
import {
  searchAlerts,
  searchChargingStationsFull,
} from "../../../actions/search.js";

// Imported assets
import CancelIcon from "../../../assets/icons/close.svg";
import CaretDownBlack from "../../../assets/icons/DownCarrot_BlackIcon.svg";
import CaretDownWhite from "../../../assets/icons/DownCarrot_WhiteIcon.svg";
import CaretUpBlack from "../../../assets/icons/UpCarrot_BlackIcon.svg";
import CaretUpWhite from "../../../assets/icons/UpCarrot_WhiteIcon.svg";
import SearchIcon from "../../../assets/icons/Search_BlackIcon.svg";

// Imported components
import FilterControls from "./components/FilterControls.js";
import Label from "../../../components/form/Label.tsx";
import NavigationBar from "../../../components/nav/NavigationBar.tsx";
import PrimaryButton from "../../../components/ui/PrimaryButton.js";

// Imported utils
import { deepCopy } from "../../../utils/misc.js";
import { isLoggedIn } from "../../../utils/user.js";

// Styled components
const AddNewNoteButton = styled.span`
  cursor: pointer;
  padding: clamp(2px, 0.5vw, 3px);
`;

const AddNewNoteButtonContainer = styled.div`
  display: flex;
  flex-direction: row;
  text-align: center;
`;

const Container = styled.div`
  align-items: center;
  background: white;
  display: flex;
  flex-direction: column;
  gap: clamp(10px, 2vw, 20px);
  justify-content: flex-start;
  padding: clamp(40px, 5vw, 80px) clamp(50px, 5vw, 100px) 0;
  position: relative;
`;

const FormField = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: clamp(2px, 0.5vw, 4px);
  justify-content: flex-start;
  padding: clamp(1px, 0.5vw, 2px);
  position: relative;
`;

const Icon = styled.img`
  height: auto;
  margin-left: clamp(5px, 1vw, 10px);
  width: clamp(10px, 1vw, 16px);
`;

const NotesCommentContainer = styled.div`
  background: white;
  border-radius: 5px;
  display: flex;
  flex-direction: column;
  gap: clamp(5px, 1vw, 10px);
  padding: clamp(10px, 2vw, 15px) clamp(8px, 2vw, 10px);
`;

const NotesCommentHeader = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;

const NotesCommentName = styled.div`
  align-self: flex-start;
  background-color: lab(from var(--terbine-blue) calc(l + 30) a b / 100);
  border-radius: 5px;
  margin: 0 clamp(5px, 1vw, 10px);
  max-width: 50%;
  padding: clamp(3px, 1vw, 5px) clamp(5px, 1.5vw, 8px);
`;

const NotesCommentText = styled.div`
  align-self: flex-start;
  margin: 0 clamp(5px, 1vw, 10px);
  max-width: 50%;
`;

const NotesCommentTimestamp = styled.div`
  align-self: center;
  margin: 0 clamp(5px, 1vw, 10px);
`;

const NotesSection = styled.div`
  background: #f2f2f2;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  margin: auto;
  width: 80%;
  gap: clamp(5px, 1vw, 10px);
  padding: clamp(8px, 2vw, 12px) clamp(16px, 3vw, 24px);
`;

const NotesToggleButton = styled.span`
  cursor: pointer;
  font-size: 1rem;
  padding: clamp(2px, 0.5vw, 3px);
`;

const NotesToggleButtonContainer = styled.div`
  display: flex;
  flex-direction: row;
  margin: clamp(3px, 1vw, 5px) 0;
  text-align: center;
  width: 70%;
`;

const SearchSection = styled.div`
  display: flex;
  flex-direction: row;
`;

const SectionContent = styled.div`
  align-self: stretch;
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: clamp(4px, 1vw, 8px);
  justify-content: center;
  min-width: clamp(600px, 80vw, 1000px);
  padding: 0 0 clamp(30px, 5vw, 60px);
  position: relative;
`;

const SectionDescription = styled.div`
  color: #000000;
  font-family: Inter, Roboto, sans-serif;
  font-size: clamp(12px, 1.5vw, 16px);
  font-weight: 400;
  line-height: clamp(18px, 2.5vw, 24px);
  position: relative;
  text-align: center;
  width: clamp(280px, 70vw, 520px);
`;

const SectionHeader = styled.div`
  align-items: center;
  align-self: stretch;
  display: flex;
  flex-direction: column;
  gap: clamp(4px, 1vw, 8px);
  justify-content: flex-start;
  position: relative;
`;

const SectionHeaderContainer = styled.div`
  padding: clamp(30px, 5vw, 60px) 0 0 0;
  display: flex;
  flex-direction: row;
  gap: clamp(20px, 5vw, 60px);
  align-items: center;
  justify-content: center;
  align-self: stretch;
  flex-shrink: 0;
  height: clamp(120px, 10vw, 161px);
  position: relative;
  min-width: clamp(600px, 80vw, 1000px);
`;

const SectionTitle = styled.div`
  color: #000000;
  font-family: Oxygen, Inter, Roboto, sans-serif;
  font-size: clamp(20px, 4vw, 40px);
  font-weight: 700;
  line-height: clamp(28px, 5vw, 48px);
  position: relative;
  text-align: center;
  width: clamp(280px, 70vw, 520px);
`;

const SortAndFilterSection = styled.div`
  align-items: flex-end;
  display: flex;
  flex-direction: row;
  gap: clamp(16px, 3vw, 32px);
`;

const SortIcon = styled.img`
  height: auto;
  margin-left: clamp(3px, 0.5vw, 5px);
  width: clamp(8px, 1vw, 12px);
`;

const TableControlsContainer = styled.div`
  align-items: flex-end;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  width: 100%;
  gap: clamp(10px, 2vw, 20px);

  & * {
    font-family: Inter, Oxygen, Roboto, sans-serif !important;
  }
`;

// Menu options for sort select menu
const sortMenuOptions = [
  {
    direction: "ASC",
    label: "Station Name (A-Z)",
    type: "Station Name",
  },
  {
    direction: "DESC",
    label: "Station Name (Z-A)",
    type: "Station Name",
  },
  {
    direction: "DESC",
    label: "Newest",
    type: "Timestamp",
  },
  {
    direction: "ASC",
    label: "Oldest",
    type: "Timestamp",
  },
];

const initialPage = 0,
  initialPageSize = 10,
  initialSortBy = "reportedAt",
  initialSortDirection = "DESC";

// Loader for router
export const loader = async () => {
  if (!isLoggedIn()) {
    return redirect("/login");
  } else {
    const alertStatusTypes: any[] = await getAlertStatusTypes();
    const equipmentTypes: any[] = await getEquipmentTypes();
    const faultTypes: any[] = await getFaultTypes();
    const initialAlerts: any[] = await searchAlerts(
      initialPage,
      99999, // initialPageSize,
      initialSortBy,
      initialSortDirection
    );

    return {
      alertStatusTypes,
      equipmentTypes,
      faultTypes,
      initialAlerts,
    };
  }
};

const ViewAlerts = () => {
  const loaderData = useLoaderData() as any;
  const appLocation = useLocation();
  const navigate = useNavigate();
  const [alertFromDash, setAlertFromDash] = useState(appLocation.state?.alert);

  const [alerts, setAlerts] = useState<any[]>([]);
  const [paginationData, setPaginationData] = useState<any[]>(
    loaderData.initialAlerts.headers
  );
  const [alertStatusTypes /*, setAlertStatusTypes*/] = useState<any[]>(
    loaderData.alertStatusTypes
  );
  const [equipmentTypes /*, setEquipmentTypes*/] = useState<any[]>(
    loaderData.equipmentTypes
  );
  const [faultTypes /*, setFaultTypes*/] = useState<any[]>(
    loaderData.faultTypes
  );

  // State to track which past alert is expanded
  const [expandedAlert, setExpandedAlert] = useState<string | null>(null);

  // Current alerts table page and rows per page setting
  const [tablePage, setTablePage] = useState<number>(initialPage);
  const [tableRowsPerPage, setTableRowsPerPage] =
    useState<number>(initialPageSize);
  const [tableSortType, setTableSortType] = useState<string>("Timestamp");
  const [tableSortDirection, setTableSortDirection] = useState<string>("DESC");

  // State for sort select menu displayed text
  const [sortBy, setSortBy] = useState<string>("Newest");

  const [search, setSearch] = useState<string>("");
  const [filters, setFilters] = useState({
    alertStatusTypes: alertStatusTypes.map((type) => type.codeName),
    equipmentTypes: equipmentTypes.map((type) => type.codeName),
    faultTypes: faultTypes.map((type) => type.codeName),
  });

  // [OLD] Current alerts table sort
  // const [tableSort, setTableSort] = useState<string>("Station Name asc");

  // Scroll page to expanded alert
  useEffect(() => {
    if (expandedAlert) {
      let row = document.querySelector(
        `#alert-row-${expandedAlert}`
      ) as HTMLElement;
      row?.scrollIntoView({ behavior: "smooth" });
    }
  }, [expandedAlert]);

  // Call backend for new alerts list when sort type or direction are changed
  useEffect(() => {
    const getNewAlerts = async () => {
      let sortType: string;

      switch (tableSortType) {
        case "Station Name": {
          sortType = "locName";
          break;
        }

        case "Timestamp":
        default: {
          sortType = "reportedAt";
          break;
        }
      }

      await searchAlerts(0, 99999, sortType, tableSortDirection)
        .then(async (resp) => {
          setAlerts(resp.data);
          setPaginationData(resp.headers);
        })
        .catch((err) => {
          // handle error
        });
    };

    if (!isLoggedIn()) {
      navigate("/login", {
        replace: true,
        state: { path: appLocation.pathname },
      });
    } else {
      getNewAlerts();
    }
  }, [/*tablePage, tableRowsPerPage, */ tableSortType, tableSortDirection]);

  // Handler to toggle past alert dropdown
  const handlePastAlertToggle = (alertId: string) => {
    setExpandedAlert((prev) => (prev === alertId ? null : alertId));
  };

  const handleSortSelect = (e) => {
    e?.preventDefault();

    setExpandedAlert(null);

    setSortBy(e.target.value);

    let sortBy = sortMenuOptions.find(
      (option) => option.label === e.target.value
    );

    setTableSortType(sortBy ? sortBy.type : "Timestamp");
    setTableSortDirection(sortBy ? sortBy.direction : "DESC");
  };

  // helper function passed into the Filter Controls component
  const handleFilterChange = (filters) => {
    setTablePage(0);
    setFilters(filters);
  };

  // Returns alerts after comparing against selected filters
  const filteredAlerts = useMemo(() => {
    if (!alerts) return null;

    let filteredAlerts = deepCopy(alerts);

    // Filter the alerts based on selected filters
    filteredAlerts = filteredAlerts.filter((alert) => {
      let alertStatusTypeMatch = false,
        faultTypeMatch = false,
        equipmentTypeMatch = false;

      alertStatusTypeMatch =
        alertStatusTypeMatch ||
        filters.alertStatusTypes.includes(alert.status.codeName);

      equipmentTypeMatch =
        equipmentTypeMatch ||
        filters.equipmentTypes.includes(alert.equipmentType.codeName);

      faultTypeMatch =
        faultTypeMatch || filters.faultTypes.includes(alert.type.codeName);

      return alertStatusTypeMatch && faultTypeMatch && equipmentTypeMatch;
    });

    return filteredAlerts;
  }, [alerts, filters]);

  // Returns alerts from filteredAlerts that pass text search
  const searchedAlerts = useMemo(() => {
    if (search) {
      if (!filteredAlerts) return null;

      let searchedAlerts = deepCopy(filteredAlerts);

      searchedAlerts = searchedAlerts.filter((alert) => {
        let fullAddress = `${alert.addressLine1}${
          alert.addressLine2 ? `, ${alert.addressLine2}` : ""
        }${alert.city ? `, ${alert.city}` : ""}${
          alert.state ? `, ${alert.state}` : ""
        }${alert.postalCd ? `, ${alert.postalCd}` : ""}${
          alert.countryCd ? `, ${alert.countryCd}` : ""
        }`;

        let dateShort = DateTime.fromISO(alert.reportedAt).toLocaleString(
          DateTime.DATE_SHORT
        );
        let timeWithSeconds = DateTime.fromISO(alert.reportedAt).toLocaleString(
          DateTime.TIME_WITH_SECONDS
        );
        let dt = `${dateShort} ${timeWithSeconds}`;

        let lowerSearch = search.toLowerCase();

        return (
          alert.locName.toLowerCase().includes(lowerSearch) ||
          alert.stationId.toLowerCase().includes(lowerSearch) ||
          fullAddress.toLowerCase().includes(lowerSearch) ||
          // alert.equipmentType.codeName.toLowerCase().includes(lowerSearch) ||
          alert.equipmentId.toLowerCase().includes(lowerSearch) ||
          // alert.type.codeName.toLowerCase().includes(lowerSearch) ||
          // alert.status.codeName.toLowerCase().includes(lowerSearch) ||
          dt.toLowerCase().includes(lowerSearch)
        );
      });

      return searchedAlerts;
    } else return filteredAlerts;
  }, [filteredAlerts, search]);

  // If user comes to this page from clicking "View Full Alert" button on Dashboard,
  // moves current page to page containing alert, plus expands
  useEffect(() => {
    if (alertFromDash && !isEmpty(searchedAlerts)) {
      setExpandedAlert(alertFromDash);

      let alertIndex = searchedAlerts.findIndex((a) => {
        console.log(a);
        return a.id === alertFromDash;
      });
      setTablePage(Math.floor(alertIndex / tableRowsPerPage));

      setAlertFromDash(null);
    }
  }, [alertFromDash, searchedAlerts, setAlertFromDash, setTablePage]);

  // Returns only alerts for current page
  const visibleAlerts = useMemo(
    () =>
      [...searchedAlerts].slice(
        tablePage * tableRowsPerPage,
        tablePage * tableRowsPerPage + tableRowsPerPage
      ),
    [
      searchedAlerts,
      tablePage,
      tableRowsPerPage,
      tableSortDirection,
      tableSortType,
    ]
  );

  const AlertRow = ({ data, i }) => {
    // Table page and rows per page for the alert's notification table
    const [tablePage, setTablePage] = useState(0);
    const [tableRowsPerPage, setTableRowsPerPage] = useState(10);
    const [notesExpanded, setNotesExpanded] = useState(false);

    const fullAddress = `${data.addressLine1}${
      data.addressLine2 ? `, ${data.addressLine2}` : ""
    }${data.city ? `, ${data.city}` : ""}${
      data.state ? `, ${data.state}` : ""
    }${data.postalCd ? `, ${data.postalCd}` : ""}${
      data.countryCd ? `, ${data.countryCd}` : ""
    }`;

    return (
      <React.Fragment key={`alert-row-${i}`}>
        <TableRow
          id={`alert-row-${data.id}`}
          onClick={() => handlePastAlertToggle(data.id)}
          sx={{
            background: "#F2F2F2",
            cursor: "pointer",
            "& td": {
              borderBottom: "none",
              color: "black",
              fontFamily: "Inter, Oxygen, Roboto, sans-serif",
            },
          }}
        >
          <Tooltip arrow followCursor placement="top" title={data.locName}>
            <TableCell
              align="left"
              sx={{
                color: "white",
                maxWidth: 200,
                minWidth: 150,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              <span>{data.locName}</span>
            </TableCell>
          </Tooltip>
          <Tooltip arrow followCursor placement="top" title={data.stationId}>
            <TableCell
              align="left"
              sx={{
                color: "white",
                maxWidth: 100,
                minWidth: 75,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {data.stationId}
            </TableCell>
          </Tooltip>
          <Tooltip arrow followCursor placement="top" title={fullAddress}>
            <TableCell
              align="left"
              sx={{
                color: "white",
                maxWidth: 200,
                minWidth: 150,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              <span>{fullAddress}</span>
            </TableCell>
          </Tooltip>
          <TableCell
            align="center"
            sx={{
              color: "white",
              maxWidth: 150,
              minWidth: 120,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {data.equipmentType.codeName}
          </TableCell>
          <Tooltip arrow followCursor placement="top" title={data.equipmentId}>
            <TableCell
              align="left"
              sx={{
                color: "white",
                maxWidth: 125,
                minWidth: 100,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {data.equipmentId}
            </TableCell>
          </Tooltip>
          <Tooltip
            arrow
            followCursor
            placement="top"
            title={data.type.codeName}
          >
            <TableCell
              align="left"
              sx={{
                color: "white",
                maxWidth: 175,
                minWidth: 125,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {data.type.codeName}
            </TableCell>
          </Tooltip>
          <Tooltip
            arrow
            followCursor
            placement="top"
            title={
              DateTime.fromISO(data.reportedAt).toLocaleString(
                DateTime.DATE_SHORT
              ) +
              " " +
              DateTime.fromISO(data.reportedAt).toLocaleString(
                DateTime.TIME_WITH_SECONDS
              )
            }
          >
            <TableCell
              align="left"
              sx={{
                color: "white",
                maxWidth: 150,
                minWidth: 120,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {DateTime.fromISO(data.reportedAt).toLocaleString(
                DateTime.DATE_SHORT
              )}{" "}
              {DateTime.fromISO(data.reportedAt).toLocaleString(
                DateTime.TIME_WITH_SECONDS
              )}
            </TableCell>
          </Tooltip>
          <TableCell
            align="center"
            sx={{
              color: "white",
              maxWidth: 125,
              minWidth: 100,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {data.status.codeName}
          </TableCell>
          <TableCell sx={{ width: 50 }}>
            <IconButton size="small" sx={{ color: "white", cursor: "pointer" }}>
              <SortIcon
                src={expandedAlert === data.id ? CaretUpBlack : CaretDownBlack}
              />
            </IconButton>
          </TableCell>
        </TableRow>
        <TableRow
          className="dashboard-alerts-expanded-row"
          sx={{ mb: expandedAlert === data.id ? "10px" : "0" }}
        >
          <TableCell
            colSpan={9}
            style={{
              borderBottom: 0,
              padding: "0 0 3px 0",
            }}
          >
            <Collapse
              in={expandedAlert === data.id}
              timeout="auto"
              unmountOnExit
            >
              <Box
                sx={{
                  background: "#FAFAFA",
                  // border: "1px solid black",
                  borderTop: "none",
                  padding: "20px",
                }}
              >
                <TableContainer
                  className={"view-alerts-notifications-table"}
                  id={`notifications-table-${i}`}
                  sx={{ border: "1px solid #d9d9d9", borderRadius: "8px", margin: "auto", width: "80%" }}
                >
                  <Table size="small">
                    <TableHead>
                      <TableRow
                        sx={{
                          background: "var(--terbine-blue)",
                          "& th": { color: "white", fontWeight: "300" },
                        }}
                      >
                        <TableCell>
                          <b>Notification Name</b>
                        </TableCell>
                        <TableCell>
                          <b>Recipient Name</b>
                        </TableCell>
                        <TableCell>
                          <b>Type</b>
                        </TableCell>
                        <TableCell>
                          <b>Status</b>
                        </TableCell>
                        <TableCell>
                          <b>Time Sent</b>
                        </TableCell>
                        <TableCell>
                          <b>Contact Info</b>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody sx={{ "& > tr:last-child > td": { borderBottom: "none" } }}>
                      {isEmpty(data.notifications) && (
                        <TableRow sx={{ background: "white" }}>
                          <TableCell colSpan={6}>
                            No notifications sent
                          </TableCell>
                        </TableRow>
                      )}

                      {!isEmpty(data.notifications) &&
                        data.notifications.map((notif, j) => (
                          <TableRow
                            key={`notifications-table-${i}-row-${j}`}
                            sx={{ background: "white" }}
                          >
                            <TableCell>
                              {notif.notificationName || "Unknown"}
                            </TableCell>
                            <TableCell>
                              {notif.contact?.contactName || "Unknown"}
                            </TableCell>
                            <TableCell>
                              {notif.notificationType.codeName}
                            </TableCell>
                            <TableCell>{notif.status.codeName}</TableCell>
                            <TableCell>
                              {DateTime.fromISO(notif.sentAt).toLocaleString(
                                DateTime.DATE_SHORT
                              ) +
                                " " +
                                DateTime.fromISO(notif.sentAt).toLocaleString(
                                  DateTime.TIME_WITH_SECONDS
                                )}
                            </TableCell>
                            <TableCell>{notif.notificationValue}</TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  component="div"
                  count={data.notifications.length}
                  onPageChange={(e, newPage) => {
                    setTablePage(newPage);
                    document
                      .getElementById(`notifications-table-${i}`)
                      ?.scrollIntoView({ behavior: "smooth" });
                  }}
                  onRowsPerPageChange={(e) => {
                    setTableRowsPerPage(parseInt(e.target.value, 10));
                    setTablePage(0);
                    document
                      .getElementById(`notifications-table-${i}`)
                      ?.scrollIntoView({ behavior: "smooth" });
                  }}
                  page={tablePage}
                  rowsPerPage={tableRowsPerPage}
                  rowsPerPageOptions={[5, 10, 25]}
                  sx={{ margin: "auto", width: "70%" }}
                />
                <NotesToggleButtonContainer>
                  <NotesToggleButton
                    onClick={() => {
                      setNotesExpanded(!notesExpanded);
                    }}
                  >
                    Notes
                    {notesExpanded ? " ⏶" : " ⏷"}
                  </NotesToggleButton>
                </NotesToggleButtonContainer>
                {notesExpanded && (
                  <NotesSection>
                    <AddNewNoteButtonContainer
                    // onClick={() => {}}
                    >
                      <AddNewNoteButton>+ Add New Note</AddNewNoteButton>
                    </AddNewNoteButtonContainer>
                    {data.comments.map((comment, i) => (
                      <NotesCommentContainer
                        key={`alert-row-${data.id}-notes-comment-${i}`}
                      >
                        <NotesCommentHeader>
                          <NotesCommentName>
                            {comment.createdBy}
                          </NotesCommentName>
                          <NotesCommentTimestamp>
                            {DateTime.fromISO(comment.createdAt).toLocaleString(
                              DateTime.DATE_SHORT
                            ) +
                              " " +
                              DateTime.fromISO(
                                comment.createdAt
                              ).toLocaleString(DateTime.TIME_WITH_SECONDS)}
                          </NotesCommentTimestamp>
                        </NotesCommentHeader>
                        <NotesCommentText>
                          {comment.commentText}
                        </NotesCommentText>
                      </NotesCommentContainer>
                    ))}
                  </NotesSection>
                )}
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      </React.Fragment>
    );
  };

  return (
    <Container>
      <NavigationBar />

      <SectionHeaderContainer>
        <SectionHeader>
          <SectionTitle>View Alerts</SectionTitle>
          <SectionDescription>View Past and Current Alerts</SectionDescription>
        </SectionHeader>
      </SectionHeaderContainer>

      <TableControlsContainer>
        <SearchSection>
          <FormField>
            <FormControl>
              <Label>Search</Label>
              <TextField
                // label="Search"
                onChange={(e) => {
                  setTablePage(0);
                  setExpandedAlert(null);
                  setSearch(e.target.value);
                }}
                size="small"
                slotProps={{
                  input: {
                    startAdornment: (
                      <Icon
                        src={SearchIcon}
                        style={{ marginLeft: "0", marginRight: "10px" }}
                      />
                    ),
                    endAdornment: search ? (
                      <Icon
                        onClick={() => setSearch("")}
                        src={CancelIcon}
                        style={{
                          cursor: "pointer",
                          marginLeft: "10px",
                          marginRight: "0",
                        }}
                      />
                    ) : (
                      <Icon style={{ marginLeft: "10px", marginRight: "0" }} />
                    ),
                  },
                }}
                value={search}
              />
            </FormControl>
          </FormField>
        </SearchSection>

        <SortAndFilterSection>
          <FormField style={{ padding: "0", width: "240px" }}>
            <FormControl fullWidth size="small">
              <Label>Sort</Label>
              <Select
                id="sort-select"
                // label="Sort"
                // labelId="sort-select-label"
                onChange={(e) => handleSortSelect(e)}
                value={sortBy}
              >
                {sortMenuOptions.map((option, i) => (
                  <MenuItem key={`sort-menu-option-${i}`} value={option.label}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </FormField>

          <FilterControls
            alertStatusTypes={alertStatusTypes}
            equipmentTypes={equipmentTypes}
            faultTypes={faultTypes}
            handleFilterChange={handleFilterChange}
          />
        </SortAndFilterSection>
      </TableControlsContainer>

      <SectionContent>
        <TableContainer
          id="alerts-table-container"
          sx={{
            borderRadius: "8px",
            margin: "0 20px 0 20px",
            scrollbarGutter: "stable both-edges",
          }}
        >
          <Table sx={{ width: "100%" }}>
            <TableHead id="view-alerts-table-head">
              <TableRow
                style={{
                  WebkitUserSelect: "none",
                  MozUserSelect: "none",
                  msUserSelect: "none",
                  userSelect: "none",
                }}
                sx={{
                  backgroundColor: "var(--terbine-blue)",
                  "& th": {
                    color: "var(--terbine-white)",
                    fontSize: "clamp(12px, 1.5vw, 18px) !important",
                    fontWeight: "300",
                  },
                }}
              >
                <TableCell
                  align="left"
                  sx={{ fontSize: "16px", maxWidth: 200, minWidth: 150 }}
                >
                  <b>Station Name</b>
                  {tableSortType === "Station Name" && (
                    <SortIcon
                      src={
                        tableSortDirection === "DESC"
                          ? CaretDownWhite
                          : CaretUpWhite
                      }
                    />
                  )}
                </TableCell>
                <TableCell align="left" sx={{ maxWidth: 100, minWidth: 75 }}>
                  <b>Station ID</b>
                </TableCell>
                <TableCell align="left" sx={{ maxWidth: 200, minWidth: 150 }}>
                  <b>Address</b>
                </TableCell>
                <TableCell align="center" sx={{ maxWidth: 150, minWidth: 120 }}>
                  <b>Equipment</b>
                </TableCell>
                <TableCell align="left" sx={{ maxWidth: 125, minWidth: 100 }}>
                  <b>Equipment ID</b>
                </TableCell>
                <TableCell align="left" sx={{ maxWidth: 175, minWidth: 125 }}>
                  <b>Fault Type</b>
                </TableCell>
                <TableCell align="left" sx={{ maxWidth: 150, minWidth: 120 }}>
                  <b>Timestamp</b>
                  {tableSortType === "Timestamp" && (
                    <SortIcon
                      src={
                        tableSortDirection === "DESC"
                          ? CaretDownWhite
                          : CaretUpWhite
                      }
                    />
                  )}
                </TableCell>
                <TableCell align="center" sx={{ maxWidth: 125, minWidth: 100 }}>
                  <b>Status</b>
                </TableCell>
                <TableCell sx={{ width: 50 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {isEmpty(visibleAlerts) ? (
                <TableRow>
                  <TableCell sx={{ width: 50 }} />
                  <TableCell>No alerts</TableCell>
                  <TableCell colSpan={7} />
                </TableRow>
              ) : (
                visibleAlerts.map((alert, i) => (
                  <AlertRow data={alert} i={i} key={`alert-row-${i}`} />
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <div style={{ alignSelf: "flex-end" }}>
          <TablePagination
            component="div"
            count={searchedAlerts.length}
            onPageChange={(e, newPage) => {
              setExpandedAlert(null);
              setTablePage(newPage);
              document
                .getElementById("alerts-table-container")
                ?.scrollIntoView({ behavior: "smooth" });
            }}
            onRowsPerPageChange={(e) => {
              setExpandedAlert(null);
              setTableRowsPerPage(parseInt(e.target.value, 10));
              setTablePage(0);
              document
                .getElementById("alerts-table-container")
                ?.scrollIntoView({ behavior: "smooth" });
            }}
            page={tablePage}
            rowsPerPage={tableRowsPerPage}
            rowsPerPageOptions={[5, 10, 25]}
          />
        </div>
      </SectionContent>
    </Container>
  );
};

export default ViewAlerts;
